/**
 * Attaches a custom, fully styleable info window to a given marker.
 * This component is meant to replace the Google Maps InfoWindow object.
 *
 * @class GoogleMapsInfoWindow
 * @author EN GARDE (Martin Holler)
 * @version 1
 */

var EN = EN || {};

EN.GoogleMapsInfoWindow = (function (_, $) {
	'use strict';

	/**
	 * @constructor
	 * @param {object} marker - Marker to append the info window to.
	 * @param {string} content - Content to place in the info window.
	 * @param {object} [options]
	 * @property {string} options.growFrom=bottom - 'bottom' or 'top'
	 * @property {boolean} options.closeButton=true - Whether or not to create a close button.
	 * @property {boolean} options.openOnClick=true - Whether or not to open the info window when the marker is clicked.
	 * @property {boolean} options.openByDefault=false - Whether or not the info window should be open by default.
	 * @property {boolean} options.autoPan=true - Whether or not to pan the info window into view if it is opened and not entirely visible.
	 * @property {object} options.classNames - Collection of class set by the class.
	 */
	function GoogleMapsInfoWindow(marker, content, options) {
		if (typeof google === undefined) {
			console.error('[GoogleMapsInfoWindow.js] Google Maps API was not found.');
			return;
		}

		if (!(marker instanceof google.maps.Marker)) {
			console.error('[GoogleMapsInfoWindow.js] The supplied marker is not a valid Google Maps marker.');
			return;
		}

		var that = this;

		that.options = _.defaultsDeep(options, that.defaults);
		that.marker = marker;
		that.content = content;

		that.create();

		// event listeners
		if (that.$close) {
			that.$close.on('click', function () {
				that.close();
			});
		}

		if (that.options.openByDefault) {
			that.open();
		}

		if (that.options.openOnClick) {
			that.marker.addListener('click', function () {
				that.open(that.options.autoPan);
			});
		}
	}

	// defaults
	GoogleMapsInfoWindow.prototype.defaults = {
		growFrom: 'bottom',
		closeButton: true,
		openOnClick: true,
		openByDefault: false,
		autoPan: true,
		classNames: {
			element: 'en-info-window en-google-style-reset',
			inner: 'en-info-window__inner',
			close: 'en-info-window__close en-icon',
			isOpen: 'en-is-open'
		}
	};

	/**
	 * Creates the info window element.
	 * @memberof GoogleMapsInfoWindow
	 * @return {jQuery}
	 */
	GoogleMapsInfoWindow.prototype.create = function () {
		var that = this;

		if (that.$element) {
			return that.$element;
		}

		that.$element = $('<div class="' + that.options.classNames.element + '"><div class="' + that.options.classNames.inner + '">' + that.content + '</div></div>');

		if (that.options.closeButton) {
			that.$close = $('<span class="' + that.options.classNames.close + '"></span>');
			that.$element.append(that.$close);
		}

		that.$element.css({
			position: 'absolute'
		});

		// prevent mouse events bubbling up to the map
		that.$element.on('mousedown click dblclick mousewheel DOMMouseScroll', function (e) {
			e.stopPropagation();
		});

		// create the OverlayView
		that.OverlayView = new google.maps.OverlayView();

		that.OverlayView.onAdd = function () {
			var panes = this.getPanes();

			panes.floatPane.appendChild(that.$element.get(0));
		};

		that.OverlayView.onRemove = function () {
			that.destroy();
		};

		that.OverlayView.draw = function () {
			if (!that.$element) {
				return;
			}

			var projection = that.OverlayView.getProjection();
			var point = projection.fromLatLngToDivPixel(that.marker.getPosition());
			var cssProperties = {
				left: point.x
			};

			if (that.options.growFrom === 'bottom') {
				cssProperties.bottom = -point.y;
			}
			else {
				cssProperties.top = point.y;
			}

			that.$element.css(cssProperties);
		};

		that.OverlayView.setMap(that.marker.getMap());

		return that.$element;
	};

	/**
	 * Deletes the info window element and all references to it.
	 * return {GoogleMapsInfoWindow}
	 */
	GoogleMapsInfoWindow.prototype.destroy = function () {
		var that = this;

		if (that.$element) {
			that.$element.remove();
			that.$element = undefined;
			that.OverlayView = undefined; // for the garbage collector
		}

		return that;
	};

	/**
	 * Opens the info window.
	 * @param {boolean} [autoPan=false] - Whether or not to pan the info window into view if it is not entirely visible.
	 * return {GoogleMapsInfoWindow}
	 */
	GoogleMapsInfoWindow.prototype.open = function (autoPan) {
		var that = this;

		that.$element.addClass(that.options.classNames.isOpen);

		if (autoPan) {
			that.fitToBounds();
		}

		return that;
	};

	/**
	 * Closes the info window.
	 * return {GoogleMapsInfoWindow}
	 */
	GoogleMapsInfoWindow.prototype.close = function () {
		var that = this;

		that.$element.removeClass(that.options.classNames.isOpen);

		return that;
	};

	/**
	 * Pans the info window into view if it is not entirely visible.
	 * @param {number} [padding=10] - Distance from the edge of the map when panning.
	 * return {GoogleMapsInfoWindow}
	 */
	GoogleMapsInfoWindow.prototype.fitToBounds = function (padding) {
		padding = padding || 10;

		var that = this;
		var map = that.marker.getMap();
		var projection = that.OverlayView.getProjection();
		var center = projection.fromLatLngToDivPixel(map.getCenter());
		var $map = $(map.getDiv());
		var offsetMap = $map.offset();
		var offsetElement = that.$element.offset();
		var distanceFromEdge = {
			top: offsetElement.top - offsetMap.top,
			bottom: offsetMap.top + $map.outerHeight() - offsetElement.top - that.$element.outerHeight(),
			left: offsetElement.left - offsetMap.left,
			right: offsetMap.left + $map.outerWidth() - offsetElement.left - that.$element.outerWidth()
		};

		if (distanceFromEdge.top < 0) {
			center.y += distanceFromEdge.top - padding;
		}

		if (distanceFromEdge.bottom < 0) {
			center.y -= distanceFromEdge.bottom - padding;
		}

		if (distanceFromEdge.left < 0) {
			center.x += distanceFromEdge.left - padding;
		}

		if (distanceFromEdge.right < 0) {
			center.x -= distanceFromEdge.right - padding;
		}

		map.panTo(projection.fromDivPixelToLatLng(center));

		return that;
	};

	return GoogleMapsInfoWindow;
}(lodash, jQuery));
